/**
 * Utility functions for linkedTable editor
 * Includes version info formatting and other helper functions
 */

import type { TableFieldApiResponse } from '@loader/shared-types';
import { parseFieldId } from '@loader/shared-types';

import { formatDateShort } from '@/lib/formatters';
import type { LinkedTableCalculatedColumn } from '@/lib/types';

/**
 * Version data structure
 */
export interface VersionData {
  id: string;
  file_name: string;
  system_tag?: 'current' | 'previous' | null;
  created_at: string;
}

/**
 * Version info structure
 */
export interface VersionInfo {
  label: string;
  sha: string | null;
  date: string | null;
  isSystem: boolean;
}

/**
 * Format date for display (re-exports centralized formatter for backward compatibility)
 */
export const formatVersionDate = formatDateShort;

/**
 * Get version info for a selection
 * Returns JSX-ready data for display
 */
export const getVersionInfo = (
  selection: string,
  versions: VersionData[]
): VersionInfo => {
  // System selections
  if (selection === 'current') {
    return { label: 'Current', sha: null, date: null, isSystem: true };
  }
  if (selection === 'previous') {
    return { label: 'Previous', sha: null, date: null, isSystem: true };
  }

  // Find specific version
  const version = versions.find((u) => u.id === selection);
  if (version) {
    const tagLabel = version.system_tag === 'current'
      ? ' [current]'
      : version.system_tag === 'previous'
        ? ' [previous]'
        : '';
    return {
      label: `${version.file_name}${tagLabel}`,
      sha: version.id,
      date: version.created_at,
      isSystem: false,
    };
  }

  // Fallback to SHA
  return {
    label: selection.substring(0, 8),
    sha: selection,
    date: null,
    isSystem: false,
  };
};

/**
 * Transform a field from API response to TableFieldApiResponse
 */
export const transformFieldFromApi = (
  field: Record<string, unknown>
): TableFieldApiResponse => {
  return {
    id: field.id as string,
    table_id: (field.table_id as string) || '',
    name: field.name as string,
    data_type: (field.data_type as TableFieldApiResponse['data_type']) || 'text',
    is_required: (field.is_required as boolean) || false,
    is_unique: (field.is_unique as boolean) || false,
    is_enabled: (field.is_enabled as boolean) ?? true,
    order_index: (field.order_index as number) || 0,
    description: (field.description as string | null) || null,
    default_value: (field.default_value as string | null) || null,
    validation_rules: (field.validation_rules as Record<string, unknown> | null) || null,
    created_at: (field.created_at as string) || '', // Use empty string if not provided
    updated_at: (field.updated_at as string) || '', // Use empty string if not provided
    table_alias: field.table_alias as string | undefined,
  };
};

/**
 * Deduplicate fields by ID
 */
export const deduplicateFields = <T extends { id: string }>(
  fields: T[]
): T[] => {
  return Array.from(new Map(fields.map((field) => [field.id, field])).values());
};

/**
 * Create a pseudo-field from a calculated column.
 *
 * Used when calculated columns need to be treated as TableFieldApiResponse
 * for UI components that expect that interface (drag overlays, selectors, etc.).
 *
 * @param calcCol - The calculated column to convert
 * @returns A TableFieldApiResponse with all required fields populated
 */
/**
 * Find a field by ID (supports qualified "alias:fieldId" format).
 * Checks available fields first, then falls back to calculated columns.
 */
export const findFieldById = (
  fieldId: string,
  availableFields: TableFieldApiResponse[],
  calculatedColumns: LinkedTableCalculatedColumn[] = []
): TableFieldApiResponse | undefined => {
  const parsed = parseFieldId(fieldId);
  const bareId = parsed.fieldId;

  // Check regular fields (match by bare ID + alias if qualified)
  const field = parsed.alias
    ? availableFields.find((f) => f.id === bareId && f.table_alias === parsed.alias)
    : availableFields.find((f) => f.id === bareId);
  if (field) return field;

  // Check calculated columns
  const calcCol = calculatedColumns.find((c) => c.id === bareId);
  if (calcCol) {
    return createPseudoFieldFromCalculatedColumn(calcCol);
  }

  return undefined;
};

/**
 * Create a pseudo-field from a calculated column.
 *
 * Used when calculated columns need to be treated as TableFieldApiResponse
 * for UI components that expect that interface (drag overlays, selectors, etc.).
 *
 * @param calcCol - The calculated column to convert
 * @returns A TableFieldApiResponse with all required fields populated
 */
export const createPseudoFieldFromCalculatedColumn = (
  calcCol: LinkedTableCalculatedColumn
): TableFieldApiResponse => {
  const now = new Date().toISOString();

  return {
    id: calcCol.id,
    name: calcCol.name,
    data_type: calcCol.data_type || 'number',
    table_id: '', // Calculated columns don't belong to a real table
    is_required: false,
    is_unique: false,
    is_enabled: true,
    default_value: null,
    validation_rules: null,
    description: calcCol.description || null,
    order_index: 0,
    created_at: now,
    updated_at: now,
  };
};

